import { ReactNode, createContext, useContext } from 'react';
import {
  TAvailableProducts,
  TGetIntroducerContextResponse,
  TGetIntroducerResponse,
  TSupportedIntroducerTypes,
} from 'lendingApi/lendingApi.types';
import {
  useQueryContext,
  useQueryIntroducer,
} from 'lendingApi/lendingApiHooks';
import { TIntroducerPermissions } from 'types';
import { getCookie } from 'utils/cookieHelper';

export type TIntroducerDataContext = {
  isLoggedIn: boolean;
  splitTests: TGetIntroducerContextResponse['data']['split_tests'] | null;
  introducerId: string | null;
  introducer: TGetIntroducerResponse['data']['introducer'] | null;
  introducerCompany:
    | TGetIntroducerResponse['data']['introducer_company']
    | null;
  context: TGetIntroducerContextResponse['data'] | null;
  introducerType: TSupportedIntroducerTypes | null;
  availableProducts: TAvailableProducts[] | null;
  hasProductsForApplication: boolean;
  contextError: Error | null;
  isContextError: boolean;
  isIntroducerError: boolean;
  isContextLoading: boolean;
  isIntroducerLoading: boolean;
  introducerStatus: 'success' | 'error' | 'pending';
  hasOnboardingFilesUploaded: boolean | null;
  checkIntroducerPermission: (permission: TIntroducerPermissions) => boolean;
};

const IntroducerDataContext = createContext<TIntroducerDataContext>({
  introducer: null,
  introducerCompany: null,
  context: null,
  isLoggedIn: false,
  splitTests: null,
  introducerType: null,
  availableProducts: null,
  hasProductsForApplication: false,
  contextError: null,
  isContextError: false,
  isIntroducerError: false,
  isContextLoading: true,
  isIntroducerLoading: true,
  introducerId: null,
  introducerStatus: 'pending',
  hasOnboardingFilesUploaded: null,
  checkIntroducerPermission: () => false,
});

export const IntroducerDataProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const {
    data: context,
    isLoading: isContextLoading,
    isError: isContextError,
    error: contextError,
  } = useQueryContext();
  const {
    data,
    isLoading: isIntroducerLoading,
    isError: isIntroducerError,
    status: introducerStatus,
  } = useQueryIntroducer();
  const introducer = data?.data;
  const splitTests: TIntroducerDataContext['splitTests'] | null =
    context?.data?.split_tests || null;

  const checkIntroducerPermission = (permission: TIntroducerPermissions) =>
    Boolean(introducer?.introducer[permission]);

  return (
    <IntroducerDataContext.Provider
      value={{
        isLoggedIn: getCookie('isLoggedIn') === 'True',
        splitTests,
        isContextLoading,
        isIntroducerLoading,
        contextError,
        isContextError,
        isIntroducerError,
        introducerStatus,
        introducerId: context?.data?.introducer_id || null,
        context: context?.data || null,
        introducer: introducer?.introducer || null,
        introducerCompany: introducer?.introducer_company || null,
        introducerType: introducer?.introducer_company.introducer_type || null,
        availableProducts:
          introducer?.introducer_company.available_products || null,
        hasProductsForApplication: (
          introducer?.introducer_company.available_products || []
        ).some(
          (product) => product === 'credit_facility' || product === 'flexi24',
        ),
        hasOnboardingFilesUploaded:
          introducer?.introducer_company.onboarding_files_uploaded || null,
        checkIntroducerPermission,
      }}
    >
      {children}
    </IntroducerDataContext.Provider>
  );
};

export const useIntroducerData = (): TIntroducerDataContext => {
  const context = useContext(IntroducerDataContext);
  if (!context) {
    throw new Error(
      'useIntroducer must be used within a IntroducerDataProvider',
    );
  }
  return context;
};
